import type { AppError, AsyncResult } from '@shared/utils';
import { err, ok, tryCatchAsync } from '@shared/utils';
import { CORE_ERROR_CODES } from '../constants/CORE_ERROR_CODES';
import { CORE_MESSAGES } from '../constants/CORE_MESSAGES';

type QueuedCall<TInstance> = {
  fn: (instance: TInstance) => Promise<any>;
  resolve: (value: any) => void;
};

type FacadeState<TInstance> = {
  instance: TInstance | null;
  bootstrapPromise: Promise<any> | null;
  callQueue: QueuedCall<TInstance>[];
};

const STATE_PREFIX = '__facade_';

/**
 * @group Core Infrastructure
 * @summary
 * <blockquote>Abstract facade that queues calls until the runtime instance is available,
 * then replays them in order once bootstrap completes.</blockquote>
 *
 * @remarks
 * State is stored on `globalThis` keyed by `facadeKey` so that all module
 * loads (e.g. Next.js instrumentation bundle vs request-handler bundle)
 * share the same singleton state. Subclasses expose a public API that
 * delegates to `callWithInstance` or `callWithInstanceResult`; callers
 * never interact with the underlying adapter directly.
 *
 * @example
 * ```typescript
 * import { RuntimeBoundFacade } from '@shared/core';
 *
 * class Logger extends RuntimeBoundFacade<LoggerRuntime> {
 *   protected readonly facadeKey = 'logger';
 *
 *   async info(message: string): AsyncResult<boolean, AppError> {
 *     return this.callWithInstance((runtime) => runtime.log('info', message));
 *   }
 * }
 * ```
 */
export abstract class RuntimeBoundFacade<TInstance> {
  protected abstract readonly facadeKey: string;

  private state(): FacadeState<TInstance> {
    const key = `${STATE_PREFIX}${this.facadeKey}__`;
    const g = globalThis as Record<string, unknown>;
    if (!g[key]) {
      g[key] = { instance: null, bootstrapPromise: null, callQueue: [] };
    }
    return g[key] as FacadeState<TInstance>;
  }

  protected setInstance(instance: TInstance): void {
    this.state().instance = instance;
    this.flushQueue();
  }

  protected getInstance(): TInstance | null {
    return this.state().instance;
  }

  setBootstrapPromise(promise: Promise<any>): void {
    this.state().bootstrapPromise = promise;
  }

  protected async callWithInstance<T>(
    fn: (instance: TInstance) => Promise<T>
  ): AsyncResult<T, AppError> {
    const s = this.state();

    if (s.instance) {
      return this.invokeFn(fn, s.instance);
    }

    if (s.bootstrapPromise) {
      const bootstrapResult = await tryCatchAsync<void, unknown>(() => s.bootstrapPromise!);

      if (s.instance) {
        return this.invokeFn(fn, s.instance);
      }

      const bootstrapError = bootstrapResult.isErr ? bootstrapResult.error : undefined;
      return err({
        errorCode: CORE_ERROR_CODES.BOOTSTRAP_NOT_COMPLETE,
        errorMessage: bootstrapError
          ? CORE_MESSAGES.BOOTSTRAP_FAILED.replace('{error}', bootstrapError instanceof Error ? bootstrapError.message : String(bootstrapError))
          : CORE_MESSAGES.BOOTSTRAP_NOT_COMPLETE,
      });
    }

    // eslint-disable-next-line @template/console-log -- @shared/core cannot import @shared/logger (circular dependency); this pre-bootstrap fallback is the only console usage in core
    console.warn(CORE_MESSAGES.FACADE_QUEUING);
    return new Promise<AsyncResult<T, AppError> extends infer R ? R : never>((resolve) => {
      s.callQueue.push({
        fn: fn as (instance: TInstance) => Promise<any>,
        resolve,
      });
    }) as any;
  }

  /**
   * Like `callWithInstance`, but for functions that already return `AsyncResult`.
   * Avoids the Result→throw→catch→Result round-trip that occurs when unwrapping
   * a Result inside a callback passed to `callWithInstance`.
   */
  protected async callWithInstanceResult<T>(
    fn: (instance: TInstance) => AsyncResult<T, AppError>
  ): AsyncResult<T, AppError> {
    const s = this.state();

    if (s.instance) {
      return fn(s.instance);
    }

    if (s.bootstrapPromise) {
      const bootstrapResult = await tryCatchAsync<void, unknown>(() => s.bootstrapPromise!);

      if (s.instance) {
        return fn(s.instance);
      }

      const bootstrapError = bootstrapResult.isErr ? bootstrapResult.error : undefined;
      return err({
        errorCode: CORE_ERROR_CODES.BOOTSTRAP_NOT_COMPLETE,
        errorMessage: bootstrapError
          ? CORE_MESSAGES.BOOTSTRAP_FAILED.replace('{error}', bootstrapError instanceof Error ? bootstrapError.message : String(bootstrapError))
          : CORE_MESSAGES.BOOTSTRAP_NOT_COMPLETE,
      });
    }

    // eslint-disable-next-line @template/console-log -- @shared/core cannot import @shared/logger (circular dependency); this pre-bootstrap fallback is the only console usage in core
    console.warn(CORE_MESSAGES.FACADE_QUEUING);
    return new Promise<AsyncResult<T, AppError> extends infer R ? R : never>((resolve) => {
      s.callQueue.push({
        fn: fn as (instance: TInstance) => Promise<any>,
        resolve,
      });
    }) as any;
  }

  private async invokeFn<T>(
    fn: (instance: TInstance) => Promise<T>,
    instance: TInstance,
  ): AsyncResult<T, AppError> {
    const result = await tryCatchAsync<T, unknown>(() => fn(instance));
    if (result.isErr) {
      const e = result.error;
      return err({
        errorCode: CORE_ERROR_CODES.FACADE_CALL_FAILED,
        errorMessage: e instanceof Error ? e.message : String(e),
      });
    }
    return ok(result.value);
  }

  private flushQueue(): void {
    const s = this.state();
    const queue = [...s.callQueue];
    s.callQueue = [];
    for (const { fn, resolve } of queue) {
      resolve(this.invokeFn(fn, s.instance!));
    }
  }

  protected resetInstance(): void {
    const key = `${STATE_PREFIX}${this.facadeKey}__`;
    const g = globalThis as Record<string, unknown>;
    g[key] = { instance: null, bootstrapPromise: null, callQueue: [] };
  }
}
